import { useEffect, useRef, useState } from 'react';
import { useDisplayStore } from '../store';
import { connectSocket } from '../socket';
import type { GameEffect } from './types';
import { ensureKeyframes } from './keyframes';
import { EFFECT_COMPONENTS, EFFECT_DURATION } from './effectComponents';
import { deriveEffectTheme, fxVars } from './effectTheme';
import { EffectThemeProvider } from './effectThemeContext';
import type { Theme, ThemeKey } from '../channels/scoreboard/themes';

// Full-viewport overlay that subscribes to game:effect and renders one
// effect at a time from a queue. Per-effect enable flags come from config.
export function AnimationHost({
  enabled, theme, themeKey,
}: { enabled?: Record<string, boolean>; theme: Theme; themeKey: ThemeKey }) {
  const token = useDisplayStore((s) => s.displayToken);
  const snapshot = useDisplayStore((s) => s.liveSnapshot);
  const [current, setCurrent] = useState<GameEffect | null>(null);
  const queue = useRef<GameEffect[]>([]);
  const seen = useRef<Set<string>>(new Set());
  const busy = useRef(false);

  useEffect(() => { ensureKeyframes(); }, []);

  function pump() {
    if (busy.current) return;
    const next = queue.current.shift();
    if (!next) return;
    busy.current = true;
    setCurrent(next);
  }

  useEffect(() => {
    if (!token) return;
    const socket = connectSocket(token, {
      onGameEffect: (fx) => {
        if (seen.current.has(fx.id)) return;          // dedup
        seen.current.add(fx.id);
        if (enabled && enabled[fx.type] === false) return; // gated off
        queue.current.push(fx);
        pump();
      },
    });
    return () => { socket.disconnect(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, enabled]);

  if (!current) return null;
  const Cmp = EFFECT_COMPONENTS[current.type];
  const finish = () => {
    busy.current = false;
    setCurrent(null);
    setTimeout(pump, 0);
  };
  if (!Cmp) {
    // No visual registered (e.g. a manual effect not yet built) — skip it
    // after its nominal duration so the queue keeps moving.
    setTimeout(finish, EFFECT_DURATION[current.type] ?? 1500);
    return null;
  }
  const effectTheme = deriveEffectTheme(theme, themeKey, snapshot);
  return (
    <EffectThemeProvider value={effectTheme}>
      <div style={{ ...fxVars(effectTheme), position: 'absolute', inset: 0, zIndex: 1000, pointerEvents: 'none' }}>
        <Cmp effect={current} snapshot={snapshot} onDone={finish} />
      </div>
    </EffectThemeProvider>
  );
}
